import type { ComponentType, MouseEventHandler, ReactNode } from 'react'
import { useCallback, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { trackClientEvent } from 'analytics/analytics'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import BodyText from 'LuxKit/Typography/BodyText'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import type { SvgIconProps } from 'LuxKit/SvgIcon'

const Row = styled.div`
  display: grid;
  grid-template-columns: ${rem(24)} minmax(0, 1fr) auto;
  align-items: center;
  gap: ${rem(12)};
`

const IconArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const Content = styled.div`
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(4)};
`

const UrgencyButton = styled.button`
  appearance: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

interface Props {
  icon: ComponentType<SvgIconProps>
  heading: ReactNode
  to?: string
  onClick?: () => void
  onDismiss?: () => void
  analyticsSubject: 'car_hire' | 'flight' | 'insurance' | 'cfmr' | 'tour' | 'transfer'
  actionLabel?: string
  urgencyLabel?: string
  onUrgencyClick?: () => void
}

function UpcomingEscapeCommercialRow(props: Props) {
  const {
    icon: Icon,
    heading,
    to,
    onClick,
    onDismiss,
    analyticsSubject,
    actionLabel,
    urgencyLabel,
    onUrgencyClick,
  } = props
  const intl = useIntl()
  const [inViewRef, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (!inView) return
    trackClientEvent({
      subject: analyticsSubject,
      action: 'impression',
      category: 'upsell_tile',
      type: 'nonInteraction',
    })
  }, [inView, analyticsSubject])

  // Same press handler as CompactOrderUpsellTile / OrderUpsellTileWithTracking.
  const clickHandler = useCallback(() => {
    trackClientEvent({
      subject: analyticsSubject,
      action: 'pressed',
      category: 'upsell_tile',
      type: 'interaction',
    })
    onClick?.()
  }, [analyticsSubject, onClick])

  const handleDismissClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    onDismiss?.()
  }, [onDismiss])

  const handleUrgencyClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    onUrgencyClick?.()
  }, [onUrgencyClick])

  const addLabel = actionLabel ?? intl.formatMessage({
    id: 'lgHUAbx1UHElgsXICFHf',
    defaultMessage: 'Add',
    description: 'Short add action label on the homepage Upcoming Escape commercial upsell row',
  })

  const dismissLabel = intl.formatMessage({
    id: '-ACI18wiqGMII80jD-fe',
    defaultMessage: 'Dismiss',
    description: 'Accessible label for dismissing a homepage Upcoming Escape commercial upsell row',
  })

  const dismissButton = !!onDismiss && (
    <IconButton
      kind="tertiary"
      size="small"
      onClick={handleDismissClick}
      aria-label={dismissLabel}
    >
      <LineTimesIcon />
    </IconButton>
  )

  return (
    <Row ref={inViewRef}>
      <IconArea>
        <Icon size="M" />
      </IconArea>
      <Content>
        <BodyText variant="medium">{heading}</BodyText>
        {urgencyLabel && (
          <UrgencyButton type="button" onClick={handleUrgencyClick}>
            <UrgencyLabel variant="speed">{urgencyLabel}</UrgencyLabel>
          </UrgencyButton>
        )}
      </Content>
      <Actions>
        {dismissButton}
        <TextButton
          kind="secondary"
          variant="default"
          size="small"
          shape="round"
          startIcon={<LinePlusIcon />}
          to={to}
          onClick={clickHandler}
        >
          {addLabel}
        </TextButton>
      </Actions>
    </Row>
  )
}

export default UpcomingEscapeCommercialRow
